import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, SkipForward, Volume2, VolumeX } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
/* ---------------- Scene definitions ---------------- */
const SCENES = [
  {
    id: 1,
    image: "/esperanza/scene1-ambulance.jpg",
    text: "No tienes por qué vivir enfermo toda la vida.",
    duration: 7000,
    textDelay: 3200, // siren plays alone for ~3s then text fades in
    siren: true,
    mood: "dark",
  },
  {
    id: 2,
    image: "/esperanza/scene2-anxiety.jpg",
    text: "No tienes por qué vivir en angustia e incertidumbre.",
    duration: 5500,
    mood: "dark",
    music: true,
  },
  {
    id: 3,
    image: "/esperanza/scene3-family-worry.jpg",
    text: "No tienes por qué vivir con temor al futuro por tus seres amados.",
    duration: 6200,
    mood: "dark",
    music: true,
  },
  {
    id: 4,
    image: "/esperanza/scene4-deterioration.jpg",
    text: "El deterioro y la desgracia no tienen que ser tu destino.",
    duration: 6000,
    mood: "dark",
    music: true,
  },
  {
    id: 5,
    image: "/esperanza/scene5-hope-future.jpg",
    text: "Existe esperanza",
    text2: "para un mejor futuro.",
    duration: 5800,
    hopeful: true,
  },
  {
    id: 6,
    isMontage: true,
    text: "Queremos ayudarte.",
    duration: 6000,
    hopeful: true,
  },
  {
    id: 7,
    image: "/esperanza/scene-walking-path.jpg",
    text: "Tu camino empieza hoy.",
    isCTA: true,
    hopeful: true,
  },
];

const MONTAGE_IMAGES = [
  "/esperanza/montage/01-vegetables.jpg",
  "/esperanza/montage/02-running.jpg",
  "/esperanza/montage/03-water-splash.jpg",
  "/esperanza/montage/04-family-laugh.jpg",
  "/esperanza/montage/05-jumping-beach.jpg",
  "/esperanza/montage/06-fruits.jpg",
  "/esperanza/montage/07-yoga-sunrise.jpg",
  "/esperanza/montage/08-grandparents-grandchild.jpg",
];

/* ---------------- Web Audio: ambulance siren synth ---------------- */
function useAmbulanceSiren(enabled) {
  useEffect(() => {
    if (!enabled) return;

    let ctx;
    let osc;
    let gain;
    let interval;
    let cleaned = false;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => { /* noop */ });
      }
      osc = ctx.createOscillator();
      osc.type = "sine";
      gain = ctx.createGain();
      gain.gain.value = 0.0;
      osc.connect(gain).connect(ctx.destination);
      osc.start();

      gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.6);

      const tones = [880, 660];
      let i = 0;
      interval = setInterval(() => {
        if (cleaned || !ctx) return;
        try {
          osc.frequency.linearRampToValueAtTime(tones[i % 2], ctx.currentTime + 0.18);
          i++;
        } catch (e) { /* noop */ }
      }, 550);
    } catch (e) {
      console.warn("Audio context failed:", e);
    }

    return () => {
      cleaned = true;
      if (interval) clearInterval(interval);
      if (gain && ctx && ctx.state !== "closed") {
        try { gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3); } catch (e) { /* noop */ }
      }
      setTimeout(() => {
        try { if (osc) osc.stop(); } catch (e) { /* noop */ }
        try { if (ctx && ctx.state !== "closed") ctx.close(); } catch (e) { /* noop */ }
      }, 400);
    };
  }, [enabled]);
}

/* ---------------- Web Audio: dramatic background music synth ---------------- */
function useDramaticMusic(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;

    let ctx;
    const oscs = [];
    let masterGain;
    let lfoInterval;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioCtx();
      // Some browsers leave the context suspended after a previous context was closed —
      // explicitly resume after the user has unlocked audio.
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => { /* noop */ });
      }

      masterGain = ctx.createGain();
      masterGain.gain.value = 0.0;
      masterGain.connect(ctx.destination);

      const freqs = [110, 164.8, 261.6];
      freqs.forEach((f) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        g.gain.value = 0.18;
        osc.connect(g).connect(masterGain);
        osc.start();
        oscs.push({ osc, g });
      });

      masterGain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 1.5);

      let phase = 0;
      lfoInterval = setInterval(() => {
        phase += 0.04;
        const wobble = Math.sin(phase) * 1.5;
        try {
          oscs[2].osc.frequency.linearRampToValueAtTime(261.6 + wobble, ctx.currentTime + 0.4);
        } catch (e) { /* noop */ }
      }, 400);
    } catch (e) {
      console.warn("Music context failed:", e);
    }

    return () => {
      if (lfoInterval) clearInterval(lfoInterval);
      if (masterGain && ctx && ctx.state !== "closed") {
        try { masterGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5); } catch (e) { /* noop */ }
      }
      setTimeout(() => {
        oscs.forEach(({ osc }) => { try { osc.stop(); } catch (e) { /* noop */ } });
        try { if (ctx && ctx.state !== "closed") ctx.close(); } catch (e) { /* noop */ }
      }, 600);
    };
  }, [enabled]);
}

/* ---------------- Web Audio: HOPEFUL bright music synth ---------------- */
function useHopefulMusic(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;

    let ctx;
    const oscs = [];
    let masterGain;
    let arpInterval;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => { /* noop */ });
      }

      masterGain = ctx.createGain();
      masterGain.gain.value = 0.0;
      masterGain.connect(ctx.destination);

      // C major chord pad (C3, E3, G3) + warm triangle wave for sweetness
      const padFreqs = [130.8, 164.8, 196.0];
      padFreqs.forEach((f) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = f;
        g.gain.value = 0.13;
        osc.connect(g).connect(masterGain);
        osc.start();
        oscs.push({ osc, g });
      });

      // Bright arpeggio synth on top (C5-E5-G5-C6 going up)
      const arpOsc = ctx.createOscillator();
      const arpGain = ctx.createGain();
      arpOsc.type = "sine";
      arpOsc.frequency.value = 523.25; // C5
      arpGain.gain.value = 0.0;
      arpOsc.connect(arpGain).connect(masterGain);
      arpOsc.start();
      oscs.push({ osc: arpOsc, g: arpGain });

      // Fade in pad
      masterGain.gain.linearRampToValueAtTime(0.10, ctx.currentTime + 1.5);

      // Bright arpeggio pattern (C5 - E5 - G5 - C6 - G5 - E5)
      const arpNotes = [523.25, 659.25, 783.99, 1046.5, 783.99, 659.25];
      let i = 0;
      arpInterval = setInterval(() => {
        const now = ctx.currentTime;
        try {
          arpOsc.frequency.setValueAtTime(arpNotes[i % arpNotes.length], now);
          arpGain.gain.cancelScheduledValues(now);
          arpGain.gain.setValueAtTime(0, now);
          arpGain.gain.linearRampToValueAtTime(0.08, now + 0.03);
          arpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
          i++;
        } catch (e) { /* noop */ }
      }, 480);
    } catch (e) {
      console.warn("Hopeful music failed:", e);
    }

    return () => {
      if (arpInterval) clearInterval(arpInterval);
      if (masterGain && ctx && ctx.state !== "closed") {
        try { masterGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5); } catch (e) { /* noop */ }
      }
      setTimeout(() => {
        oscs.forEach(({ osc }) => { try { osc.stop(); } catch (e) { /* noop */ } });
        try { if (ctx && ctx.state !== "closed") ctx.close(); } catch (e) { /* noop */ }
      }, 600);
    };
  }, [enabled]);
}

/* ---------------- Montage scene component ---------------- */
function MontageScene({ active }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(() => {
      setIdx((i) => (i + 1) % MONTAGE_IMAGES.length);
    }, 280);
    return () => clearInterval(interval);
  }, [active]);

  if (!active) return null;

  return (
    <div className="absolute inset-0 overflow-hidden">
      {MONTAGE_IMAGES.map((src, i) => (
        <div
          key={src}
          className="absolute inset-0 transition-opacity duration-100"
          style={{
            opacity: i === idx ? 1 : 0,
            backgroundImage: `url(${src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      ))}
      {/* Light vignette only — keep images vibrant */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/50" />
    </div>
  );
}

/* ---------------- Component ---------------- */
export default function Esperanza() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [started, setStarted] = useState(true); // auto-start, no click required
  const [audioOn, setAudioOn] = useState(true);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const timerRef = useRef(null);

  const scene = SCENES[step];

  // Ambulance siren — only when the current scene has siren:true AND audio unlocked
  const sirenActive = started && scene?.siren && audioOn && audioUnlocked;
  useAmbulanceSiren(sirenActive);

  // Dramatic music — scenes 2-4 (dark scenes)
  const musicActive = started && scene?.music && audioOn && audioUnlocked;
  useDramaticMusic(musicActive);

  // Hopeful bright music — scenes 5-7 (esperanza, montage, CTA)
  const hopefulActive = started && scene?.hopeful && audioOn && audioUnlocked;
  useHopefulMusic(hopefulActive);

  // Browser autoplay policy: user must explicitly click the unlock overlay
  // (handled by the button below — no auto-unlock listener so audio always plays from start)

  // Track text fade-in delay (used by scene 1)
  const [textVisible, setTextVisible] = useState(false);
  useEffect(() => {
    if (!started || !scene) return undefined;
    const delay = scene.textDelay || 0;
    const t = setTimeout(() => setTextVisible(true), delay);
    return () => {
      clearTimeout(t);
      setTextVisible(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, started]);

  // Auto-advance scenes
  useEffect(() => {
    if (!started) return;
    if (step >= SCENES.length - 1) return; // last scene is the CTA, stays
    const sc = SCENES[step];
    timerRef.current = setTimeout(() => setStep((s) => s + 1), sc.duration);
    return () => clearTimeout(timerRef.current);
  }, [step, started]);

  useEffect(() => {
    trackEvent("esperanza_view", { event_category: "engagement" });
  }, []);

  const skip = () => {
    clearTimeout(timerRef.current);
    setStep(SCENES.length - 1);
    trackEvent("esperanza_skipped", { event_category: "engagement" });
  };

  const goToQuiz = () => {
    trackEvent("esperanza_cta_click", { event_category: "conversion" });
    navigate("/#quiz");
    setTimeout(() => {
      const el = document.getElementById("quiz");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <main className="fixed inset-0 z-50 bg-[#0a0e0d] text-[#f9f6f0] overflow-hidden">
      <Helmet>
        <title>Si tienes vida, tienes esperanza · Eliora Longevity</title>
        <meta name="description" content="No tienes por qué vivir enfermo el resto de tu vida. Diabetes, hipertensión, tiroides, gastritis. Hay un camino de vuelta a la salud." />
      </Helmet>

      {/* Audio unlock — full overlay, must be clicked to start sound experience */}
      {!audioUnlocked && (
        <button
          onClick={() => {
            // Resume any suspended context AND restart from scene 1 so siren plays from start
            try {
              const AC = window.AudioContext || window.webkitAudioContext;
              const c = new AC();
              c.resume().then(() => c.close()).catch(() => c.close());
            } catch (e) { /* noop */ }
            setAudioUnlocked(true);
            setStep(0);
            clearTimeout(timerRef.current);
          }}
          data-testid="esperanza-audio-unlock"
          className="absolute inset-0 z-40 grid place-items-center bg-[#0a0e0d]/95 backdrop-blur cursor-pointer group"
        >
          <div className="flex flex-col items-center gap-5 text-[#f9f6f0] group-hover:text-[#bc6c25] transition-colors">
            <div className="w-28 h-28 rounded-full border-2 border-[#f9f6f0]/40 group-hover:border-[#bc6c25] grid place-items-center transition-all animate-pulse">
              <Volume2 className="w-10 h-10" />
            </div>
            <p className="text-sm uppercase tracking-[0.4em] font-semibold">Toca para empezar</p>
            <p className="text-xs text-[#f9f6f0]/40 uppercase tracking-[0.28em]">🔊 Sube el volumen</p>
          </div>
        </button>
      )}

      {/* Background image / montage — fade between scenes */}
      {SCENES.map((sc, i) => {
        if (sc.isMontage) {
          return <MontageScene key={sc.id} active={started && step === i} />;
        }
        return (
          <div
            key={sc.id}
            className="absolute inset-0 transition-opacity duration-[1800ms] ease-in-out"
            style={{
              opacity: started && step === i ? 1 : 0,
              backgroundImage: `url(${sc.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            aria-hidden="true"
          />
        );
      })}

      {/* Dark overlay for legibility (skip during montage) */}
      {started && scene && !scene.isMontage && (
        <div
          className={`absolute inset-0 transition-all duration-1000 ${scene?.mood === "dark" ? "bg-black/78" : "bg-black/55"}`}
          aria-hidden="true"
        />
      )}

      {/* Scene text */}
      {started && scene && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
          {scene.isCTA ? (
            <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-1000" key={`cta-${step}`}>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#bc6c25] mb-4">Eliora Longevity</p>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-tight text-[#f9f6f0]">{scene.text}</h2>
              <p className="text-base md:text-xl text-[#f9f6f0]/80 mt-8 max-w-xl mx-auto leading-relaxed">
                Hagamos juntos un protocolo personalizado. <br />
                Tarda 3 minutos. Es <strong className="text-[#bc6c25]">CORTESÍA</strong>.
              </p>
              <button
                onClick={goToQuiz}
                data-testid="esperanza-cta-btn"
                className="mt-10 inline-flex items-center gap-3 px-10 py-5 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white font-semibold tracking-wide transition-all lift shadow-2xl shadow-[#bc6c25]/30"
              >
                Hacer el Quiz Wellness <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate("/")}
                className="block mx-auto mt-8 text-xs text-[#f9f6f0]/40 hover:text-[#f9f6f0]/70 underline underline-offset-4"
              >
                Volver al inicio
              </button>
            </div>
          ) : (
            <div
              key={`scene-${step}`}
              className={`max-w-3xl text-center transition-opacity duration-1000 ${textVisible ? "opacity-100 animate-in fade-in slide-in-from-bottom-4 duration-1000" : "opacity-0"}`}
            >
              <h2 className={`font-serif leading-tight text-[#f9f6f0] ${scene.isMontage ? "text-5xl md:text-7xl lg:text-8xl" : "text-3xl md:text-5xl lg:text-6xl"}`}>
                {scene.text}
                {scene.text2 && (
                  <span className="block mt-2 text-[#bc6c25]">{scene.text2}</span>
                )}
              </h2>
            </div>
          )}
        </div>
      )}

      {/* Progress bar */}
      {started && !scene?.isCTA && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10 z-20">
          <div
            key={step}
            className="h-full bg-[#bc6c25] origin-left"
            style={{ animation: `progressFill ${scene.duration}ms linear forwards` }}
          />
        </div>
      )}

      {/* Audio toggle */}
      {started && (
        <button
          onClick={() => setAudioOn((v) => !v)}
          data-testid="esperanza-audio-toggle"
          className="absolute top-6 left-6 z-20 p-2.5 rounded-full bg-white/5 hover:bg-white/15 text-[#f9f6f0]/70 hover:text-[#f9f6f0] backdrop-blur transition-colors"
          aria-label="Audio"
        >
          {audioOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      )}

      {/* Skip button */}
      {started && !scene?.isCTA && (
        <button
          onClick={skip}
          data-testid="esperanza-skip-btn"
          className="absolute top-6 right-6 z-20 px-4 py-2 rounded-full bg-white/5 hover:bg-white/15 text-[#f9f6f0]/70 hover:text-[#f9f6f0] text-xs uppercase tracking-[0.18em] backdrop-blur transition-colors flex items-center gap-2"
        >
          Saltar <SkipForward className="w-3 h-3" />
        </button>
      )}

      {/* Scene dots */}
      {started && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all ${i === step ? "bg-[#bc6c25] w-7" : "bg-white/30 hover:bg-white/60 w-1.5"}`}
              aria-label={`Escena ${s.id}`}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes progressFill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </main>
  );
}
